import React, { useContext, useEffect, useState, useRef } from "react";
import { ProjectContext } from "../../context/project_context";
import TimecodeInput from "../../components/TimecodeInput";
import InsertRowModal from "../../components/InsertRowModal";
import SplitRowModal from "../../components/SplitRowModal";
import debounce from 'lodash/debounce';
import { countDuplicateEnglishTranscripts } from "../../utils/english_transcript_duplicates";

const ProjectSentenceBuilding = () => {
  const { 
    project, 
    updateProject, 
    english_transcripts, 
    fetchEnglishTranscripts,
    setEnglishTranscripts,
    mergeEnglishTranscripts,
    updateEnglishTranscriptTimecodes,
    insertEnglishTranscript,
    splitEnglishTranscript,
    deleteEnglishTranscript,
    removeDuplicateEnglishTranscripts,
    // Media playback
    activeTranscriptId,
    autoScrollEnabled,
    seekToTranscript,
    isPlaying,
    mediaRef
  } = useContext(ProjectContext);

  // Insert row modal state
  const [showInsertModal, setShowInsertModal] = useState(false);
  const [insertPosition, setInsertPosition] = useState({ startTime: 0, endTime: 1 });
  const [savingStates, setSavingStates] = useState({});

  // Split row modal state
  const [showSplitModal, setShowSplitModal] = useState(false);
  const [splitTranscript, setSplitTranscript] = useState(null);

  // Refs for auto-scrolling
  const rowRefs = useRef({});

  useEffect(() => {
    if (project?.id) {
      fetchEnglishTranscripts();
    }
  }, [project?.id]);

  // Auto-scroll to active row
  useEffect(() => {
    if (autoScrollEnabled && activeTranscriptId && rowRefs.current[activeTranscriptId]) {
      rowRefs.current[activeTranscriptId].scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }
  }, [activeTranscriptId, autoScrollEnabled]);

  // Listen for focus active transcript event (Shift+Enter shortcut)
  useEffect(() => {
    const handleFocusActive = () => {
      if (activeTranscriptId && rowRefs.current[activeTranscriptId]) {
        const row = rowRefs.current[activeTranscriptId];
        row.scrollIntoView({ behavior: 'smooth', block: 'center' });
        // Focus the checkbox in the row
        const checkbox = row.querySelector('input[type="checkbox"]');
        if (checkbox) {
          checkbox.focus();
        }
      }
    };

    window.addEventListener('focusActiveTranscript', handleFocusActive);
    return () => window.removeEventListener('focusActiveTranscript', handleFocusActive);
  }, [activeTranscriptId]);

  // Listen for seek to focused transcript event (Ctrl+Enter shortcut)
  useEffect(() => {
    const handleSeekToFocused = () => {
      // Find which row's element is currently focused
      const focusedElement = document.activeElement;
      const row = focusedElement?.closest('tr');
      if (row) {
        const transcriptId = Object.keys(rowRefs.current).find(
          id => rowRefs.current[id] === row
        );
        if (transcriptId) {
          const transcript = english_transcripts.find(t => t.id === parseInt(transcriptId));
          if (transcript) {
            seekToTranscript(transcript);
          }
        }
      }
    };

    window.addEventListener('seekToFocusedTranscript', handleSeekToFocused);
    return () => window.removeEventListener('seekToFocusedTranscript', handleSeekToFocused);
  }, [english_transcripts, seekToTranscript]);

  const handleComplete = async () => {
    await updateProject({
      ...project,
      project_status: "sentence_thought_fixing"
    });
  };

  const handleBack = async () => {
    await updateProject({
      ...project,
      project_status: "fix_transcript"
    });
  };

  const [editedTranscripts, setEditedTranscripts] = useState({});

  // Move debounce outside of component to prevent recreation on each render
  const debouncedSave = debounce((transcriptId, newContent) => {
    handleSaveEnglishTranscript(transcriptId, newContent);
  }, 2000);

  const handleContentChange = (transcriptId, newContent) => {
    setEditedTranscripts(prev => ({
      ...prev,
      [transcriptId]: newContent
    }));

    debouncedSave(transcriptId, newContent);
  };

  const handleTimecodeChange = async (transcriptId, field, newValue) => {
    const transcript = english_transcripts.find(t => t.id === transcriptId);
    if (!transcript) return;

    const startTime = field === 'start_time' ? newValue : parseFloat(transcript.start_time);
    const endTime = field === 'end_time' ? newValue : parseFloat(transcript.end_time);

    if (endTime <= startTime) {
      alert('End time must be greater than start time');
      return;
    }

    try {
      setSavingStates(prev => ({ ...prev, [`timecode_${transcriptId}`]: true }));
      await updateEnglishTranscriptTimecodes(transcriptId, startTime, endTime);
    } catch (error) {
      console.error('Failed to update timecodes:', error);
      alert('Failed to update timecodes');
    } finally {
      setSavingStates(prev => ({ ...prev, [`timecode_${transcriptId}`]: false }));
    }
  };

  const handleKeyDown = (e, currentIndex, transcriptId) => {
    const textareas = document.querySelectorAll('.english-transcript-textarea');

    switch (e.key) {
      case 'ArrowUp':
        if (currentIndex > 0) {
          textareas[currentIndex - 1].focus();
        }
        break;
      case 'ArrowDown':
        if (currentIndex < textareas.length - 1) {
          textareas[currentIndex + 1].focus();
        }
        break;
      case 'Enter':
        if (!e.shiftKey && !e.ctrlKey) {
          e.preventDefault();
          if (currentIndex < textareas.length - 1) {
            textareas[currentIndex + 1].focus();
          }
        }
        break;
    }
  };

  // Add new state for tracking selected rows
  const [selectedRows, setSelectedRows] = useState(new Set());

  // Add helper function to check if rows are adjacent
  const areRowsAdjacent = (selectedIds, newId, english_transcripts) => {
    if (selectedIds.size === 0) return true;
    
    const selectedIndexes = Array.from(selectedIds).map(id => 
      english_transcripts.findIndex(t => t.id === id)
    );
    const newIndex = english_transcripts.findIndex(t => t.id === newId);
    
    const minSelected = Math.min(...selectedIndexes);
    const maxSelected = Math.max(...selectedIndexes);
    
    return newIndex === minSelected - 1 || newIndex === maxSelected + 1;
  };

  const updateMergedTranscripts = (mergedData) => {
    const { merged_transcript, removed_transcript_ids } = mergedData;
    
    const updatedTranscripts = english_transcripts.filter(t => 
      !removed_transcript_ids.includes(t.id)
    ).map(t => 
      t.id === merged_transcript.id ? merged_transcript : t
    );

    const newEditedTranscripts = { ...editedTranscripts };
    removed_transcript_ids.forEach(id => {
      delete newEditedTranscripts[id];
    });
    if (merged_transcript.english_text) {
      newEditedTranscripts[merged_transcript.id] = merged_transcript.english_text;
    }

    setEditedTranscripts(newEditedTranscripts);
    setEnglishTranscripts(updatedTranscripts);
  };

  const handleMergeRows = async () => {
    const selectedArray = Array.from(selectedRows).sort((a, b) => a - b);
    
    if (selectedArray.length < 2) return;

    const selectedTranscripts = selectedArray.map(id => ({
      id: id,
      text: english_transcripts.find(t => t.id === id).text,
      english_text: editedTranscripts[id] ?? english_transcripts.find(t => t.id === id).english_text
    }));

    try {
      const data = await mergeEnglishTranscripts(selectedArray, selectedTranscripts);
      updateMergedTranscripts(data);
      setSelectedRows(new Set());
    } catch (error) {
      console.error('Error merging transcripts:', error);
    }
  };

  const handleInsertRow = (afterTranscript) => {
    let startTime = 0;
    let endTime = 1;

    if (afterTranscript) {
      startTime = parseFloat(afterTranscript.end_time) || 0;
      const currentIndex = english_transcripts.findIndex(t => t.id === afterTranscript.id);
      const nextTranscript = english_transcripts[currentIndex + 1];
      if (nextTranscript) {
        endTime = parseFloat(nextTranscript.start_time) || startTime + 1;
      } else {
        endTime = startTime + 2;
      }
    }

    setInsertPosition({ startTime, endTime });
    setShowInsertModal(true);
  };

  const handleInsertConfirm = async (startTime, endTime, text) => {
    await insertEnglishTranscript(startTime, endTime, text, text);
  };

  const handleSplitRow = (transcript) => {
    setSplitTranscript(transcript);
    setShowSplitModal(true);
  };

  const handleSplitConfirm = async (splitTime, firstText, secondText) => {
    if (!splitTranscript?.id) throw new Error('No transcript selected to split');
    await splitEnglishTranscript(splitTranscript.id, splitTime, firstText, secondText);
  };

  const handleDeleteRow = async (transcriptId) => {
    if (window.confirm('Are you sure you want to delete this transcript row?')) {
      await deleteEnglishTranscript(transcriptId);
    }
  };

  const duplicateCount = countDuplicateEnglishTranscripts(english_transcripts);

  const handleRemoveDuplicates = async () => {
    if (duplicateCount === 0) return;
    if (!window.confirm(
      `Remove ${duplicateCount} duplicate row(s)? Rows with the same timeframe and original text will be deleted. The earliest row in each group will be kept.`
    )) return;
    await removeDuplicateEnglishTranscripts();
  };

  const handleRowClick = (transcript) => {
    seekToTranscript(transcript);
  };

  return (
    <div className="project-sentence-building">
      <h2>Sentence Building</h2>
      <div className="sentence-content">
        <p>Project Name: {project.name}</p>
        
        <div className="transcripts-list mt-3">
          <div className="d-flex gap-2 mb-2">
            <button
              className="btn btn-sm btn-outline-primary"
              onClick={() => handleInsertRow(null)}
            >
              + Insert Row at Beginning
            </button>
            <button 
              className="btn btn-sm btn-secondary"
              onClick={handleMergeRows}
              disabled={selectedRows.size < 2}
            >
              Merge Selected Rows ({selectedRows.size})
            </button>
            <button
              className="btn btn-sm btn-outline-danger"
              onClick={handleRemoveDuplicates}
              disabled={duplicateCount === 0}
            >
              Remove Duplicates ({duplicateCount})
            </button>
          </div>
          
          <table className="table table-bordered no-padding">
            <thead>
              <tr>
                <th style={{ width: '50px' }}>Select</th>
                <th style={{ width: '50px' }}>T#</th>
                <th style={{ width: '240px' }}>Timecodes</th>
                <th>Original Text</th>
                <th style={{ width: '180px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {english_transcripts.map((transcript, index) => {
                const isActive = transcript.id === activeTranscriptId;
                const isSelected = selectedRows.has(transcript.id);
                
                let rowClass = '';
                if (isActive) {
                  rowClass = 'table-info';
                } else if (isSelected) {
                  rowClass = 'table-primary';
                }

                return (
                  <tr 
                    key={transcript.id}
                    ref={el => rowRefs.current[transcript.id] = el}
                    className={rowClass}
                    style={{ 
                      cursor: 'pointer',
                      transition: 'background-color 0.3s ease'
                    }}
                    onClick={(e) => {
                      if (e.target.type === 'checkbox') return;
                      
                      const newSelected = new Set(selectedRows);
                      if (!selectedRows.has(transcript.id)) {
                        if (areRowsAdjacent(selectedRows, transcript.id, english_transcripts)) {
                          newSelected.add(transcript.id);
                          setSelectedRows(newSelected);
                        }
                      } else {
                        const selectedIndexes = Array.from(selectedRows).map(id => 
                          english_transcripts.findIndex(t => t.id === id)
                        );
                        const currentIndex = english_transcripts.findIndex(t => t.id === transcript.id);
                        if (currentIndex === Math.min(...selectedIndexes) || 
                            currentIndex === Math.max(...selectedIndexes)) {
                          newSelected.delete(transcript.id);
                          setSelectedRows(newSelected);
                        }
                      }
                    }}
                  >
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedRows.has(transcript.id)}
                        onChange={(e) => {
                          const newSelected = new Set(selectedRows);
                          if (e.target.checked) {
                            if (areRowsAdjacent(selectedRows, transcript.id, english_transcripts)) {
                              newSelected.add(transcript.id);
                              setSelectedRows(newSelected);
                            }
                          } else {
                            const selectedIndexes = Array.from(selectedRows).map(id => 
                              english_transcripts.findIndex(t => t.id === id)
                            );
                            const currentIndex = english_transcripts.findIndex(t => t.id === transcript.id);
                            if (currentIndex === Math.min(...selectedIndexes) || 
                                currentIndex === Math.max(...selectedIndexes)) {
                              newSelected.delete(transcript.id);
                              setSelectedRows(newSelected);
                            }
                          }
                        }}
                      />
                    </td>
                    <td 
                      style={{ width: '50px' }}
                      onClick={() => handleRowClick(transcript)}
                    >
                      {transcript.sequence_number}
                    </td>
                    <td style={{ width: '240px' }}>
                      <div className="d-flex flex-column gap-1">
                        <div className="d-flex align-items-center gap-1">
                          <small className="text-muted" style={{ width: '35px' }}>Start:</small>
                          <TimecodeInput
                            value={parseFloat(transcript.start_time)}
                            onChange={(val) => handleTimecodeChange(transcript.id, 'start_time', val)}
                          />
                        </div>
                        <div className="d-flex align-items-center gap-1">
                          <small className="text-muted" style={{ width: '35px' }}>End:</small>
                          <TimecodeInput
                            value={parseFloat(transcript.end_time)}
                            onChange={(val) => handleTimecodeChange(transcript.id, 'end_time', val)}
                          />
                        </div>
                        {savingStates[`timecode_${transcript.id}`] && (
                          <small className="text-muted">Saving...</small>
                        )}
                      </div>
                    </td>
                    <td className="p-2">
                      {transcript.original_text}
                    </td>
                    <td style={{ width: '180px' }}>
                      <div className="d-flex flex-column gap-1">
                        {editedTranscripts[transcript.id] && (
                          <span className="text-muted small">Saving...</span>
                        )}
                        <div className="btn-group btn-group-sm">
                          <button
                            className="btn btn-outline-success btn-sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              seekToTranscript(transcript);
                              // Start playing if not already playing
                              if (!isPlaying && mediaRef.current) {
                                mediaRef.current.play();
                              }
                            }}
                            title="Play from this timecode"
                          >
                            ▶
                          </button>
                          <button
                            className="btn btn-outline-warning btn-sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSplitRow(transcript);
                            }}
                            title="Split this row"
                          >
                            Split
                          </button>
                          <button
                            className="btn btn-outline-primary btn-sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleInsertRow(transcript);
                            }}
                            title="Insert row after this one"
                          >
                            +
                          </button>
                          <button
                            className="btn btn-outline-danger btn-sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteRow(transcript.id);
                            }}
                            title="Delete this row"
                          >
                            ×
                          </button>
                        </div>
                        {selectedRows.size >= 2 && selectedRows.has(transcript.id) && (
                          <button 
                            className="btn btn-sm btn-secondary"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleMergeRows();
                            }}
                          >
                            Merge
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="d-flex gap-2 mt-3">
          <button 
            className="btn btn-secondary"
            onClick={handleBack}
          >
            Back to Fix Transcript
          </button>
          <button 
            className="btn btn-primary"
            onClick={handleComplete}
          >
            Next to Sentence Thought Fixing
          </button>
        </div>
      </div>

      {/* Insert Row Modal */}
      <InsertRowModal
        isOpen={showInsertModal}
        onClose={() => setShowInsertModal(false)}
        onInsert={handleInsertConfirm}
        defaultStartTime={insertPosition.startTime}
        defaultEndTime={insertPosition.endTime}
        type="english_transcript"
      />

      {/* Split Row Modal */}
      <SplitRowModal
        isOpen={showSplitModal}
        onClose={() => {
          setShowSplitModal(false);
          setSplitTranscript(null);
        }}
        onSplit={handleSplitConfirm}
        transcript={splitTranscript}
      />
    </div>
  );
};

export default ProjectSentenceBuilding;
